
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AnalyticsCard } from "./AnalyticsCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DailyStats {
  date: string;
  total_winners: number;
  university_name?: string;
}

interface University {
  id: number;
  name: string;
  campaign_start_date: string;
  campaign_end_date: string;
}

interface Stats {
  tipsCount: number;
  prizesCount: number;
  actualPrizesCount: number;
  dailyStats: DailyStats[];
  totalPrizes: number;
  universities: University[];
}

export function DashboardAnalytics() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      console.log('Fetching dashboard statistics...');
      
      try {
        // Fetch basic stats and universities
        const [
          { count: tipsCount },
          { count: prizesCount },
          { count: actualPrizesCount },
          { data: universities, error: universitiesError }
        ] = await Promise.all([
          supabase.from('tips').select('*', { count: 'exact', head: true }),
          supabase.from('prizes').select('*', { count: 'exact', head: true }),
          supabase.from('winners')
            .select('*', { count: 'exact', head: true })
            .not('prize_id', 'is', null),
          supabase.from('universities').select('*')
        ]);

        if (universitiesError) throw universitiesError;

        // Fetch daily totals from winners_sydney_time view
        const { data: dailyStats, error: dailyError } = await supabase
          .rpc('get_daily_votes_sydney');

        if (dailyError) {
          console.error('Error fetching daily data:', dailyError);
          throw dailyError;
        }

        // Create a map of all dates within campaign periods
        const allDates = new Set<string>();
        universities.forEach(uni => {
          const start = new Date(uni.campaign_start_date);
          const end = new Date(uni.campaign_end_date);
          for (let date = start; date <= end; date.setDate(date.getDate() + 1)) {
            allDates.add(date.toISOString().split('T')[0]);
          }
        });

        // Format daily stats with university information
        const formattedDailyStats = Array.from(allDates).map(date => {
          const existingStat = dailyStats?.find(stat => stat.date === date);
          const relevantUniversity = universities.find(uni => {
            const dateObj = new Date(date);
            return dateObj >= new Date(uni.campaign_start_date) && 
                   dateObj <= new Date(uni.campaign_end_date);
          });

          return {
            date,
            total_winners: existingStat ? Number(existingStat.votes) : 0,
            university_name: relevantUniversity?.name
          };
        }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        console.log('Formatted daily stats:', formattedDailyStats);

        setStats({
          tipsCount: tipsCount || 0,
          prizesCount: prizesCount || 0,
          actualPrizesCount: actualPrizesCount || 0,
          dailyStats: formattedDailyStats,
          totalPrizes: formattedDailyStats.reduce((sum, day) => sum + day.total_winners, 0),
          universities: universities
        });

      } catch (error) {
        console.error('Error fetching dashboard statistics:', error);
      }
    };

    fetchStats();

    // Set up real-time subscription for prize updates
    const channel = supabase
      .channel('dashboard-prizes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'winners'
      }, () => {
        fetchStats(); // Refresh stats when prizes change
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (!stats) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <AnalyticsCard title="Loading..." value="..." />
      </div>
    );
  }

  // Group daily stats by university
  const groupedStats = stats.dailyStats.reduce((acc, stat) => {
    const key = stat.university_name || 'Unknown';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(stat);
    return acc;
  }, {} as Record<string, DailyStats[]>);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <AnalyticsCard title="Total Tips" value={stats.tipsCount} />
        <AnalyticsCard title="Available Prizes" value={stats.prizesCount} />
        <AnalyticsCard title="Claimed Prizes" value={stats.actualPrizesCount} />
        <AnalyticsCard title="Total Prizes Won" value={stats.totalPrizes} />
      </div>
      
      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Total Winners</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(groupedStats).map(([universityName, dates]) => {
              const universityTotal = dates.reduce((sum, day) => sum + day.total_winners, 0);
              
              return (
                <React.Fragment key={universityName}>
                  <TableRow className="bg-gray-100">
                    <TableCell colSpan={2} className="font-semibold">
                      {universityName}
                    </TableCell>
                  </TableRow>
                  {dates.map((day) => (
                    <TableRow key={day.date} className="hover:bg-gray-50">
                      <TableCell>{new Date(day.date).toLocaleDateString('en-AU', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}</TableCell>
                      <TableCell className="text-right font-medium">
                        {day.total_winners.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell>Total for {universityName}</TableCell>
                    <TableCell className="text-right">
                      {universityTotal.toLocaleString()}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
